import React, {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
} from 'react';


import {ImageSelect} from 'src/components/image-select';
import {FormInput, NumberInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {SettingsType as SettingsTypes} from 'src/openapi/admin/models/SettingsType';

import {Group} from '@pb/kit-component-group';

import {
    ADVERTISEMENT_DISPLAY_TIME_CONTROL,
    ADVERTISEMENT_IMAGE_CONTROL,
    ADVERTISEMENT_URL_CONTROL,
} from '../constants';

import {AdvertisementPopupSkeleton} from './advertisement-popup.skeleton';
import {useValidateAdvertisement} from './useValidateAdvertisement';

import type {AdvertisementsSettings} from 'src/openapi/admin/models/AdvertisementsSettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {SettingsType} from 'src/openapi/admin/models/SettingsType';

type FileWithPreview = File & {preview?: string};

const getFilePreview = (file: File | null): string | null => {
    if (file && typeof (file as FileWithPreview).preview === 'string') {
        return (file as FileWithPreview).preview ?? null;
    }

    return null;
};

type Props = {
    type: SettingsType,
    isLoading: boolean,
    loadingError: Error | null,
    readonly?: boolean,

    image: File | null,
    setImage: (file: File | null) => void,
    settings: AdvertisementsSettings,
    setSettings: Dispatch<SetStateAction<AdvertisementsSettings>>,
};

export const AdvertisementPopup = (props: Props) => {
    const {
        type,
        isLoading,
        loadingError,
        readonly,
        image,
        setImage,
        settings,
        setSettings,
    } = props;

    const {editValidation} = useValidateAdvertisement();

    useEffect(() => {
        editValidation({
            image,
            ...settings,
        });
    }, [image, settings, editValidation]);

    const removeAdvertisementImage = useCallback(() => {
        setImage(null);
    }, [setImage]);

    const downloadAdvertisementImage = useCallback(() => {
        if (image) {
            const anchor = document.createElement('a');
            const preview = getFilePreview(image);

            if (preview) {
                anchor.href = preview;
                anchor.download = image.name;
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            }
        }
    }, [image]);

    const changeAdvertisementLink = useCallback((link: string) => {
        setSettings(prevState => ({
            ...prevState,
            link,
        }));
    }, [setSettings]);

    const changeAdvertisementShowingTime = useCallback((showingTime?: number) => {
        setSettings(prevState => ({
            ...prevState,
            showingTime,
        }));
    }, [setSettings]);

    return (
        <Skeleton shown={isLoading} view={<AdvertisementPopupSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}>
                <Group direction="v">
                    <ValidatedControl name={`${type}-${ADVERTISEMENT_IMAGE_CONTROL}`}>
                        {() => (
                            <ImageSelect
                                readonly={readonly}
                                width={360}
                                height={150}
                                maxSize={2}
                                label={type === SettingsTypes.MOBILE ? 'Mobile Banner' : 'Web Banner'}
                                alt={type === SettingsTypes.MOBILE ? 'Mobile Banner' : 'Web Banner'}
                                img={getFilePreview(image)}
                                onRemove={removeAdvertisementImage}
                                onDrop={setImage}
                                onDownload={downloadAdvertisementImage}
                            />
                        )}
                    </ValidatedControl>
                    <CustomValidatedControl name={`${type}-${ADVERTISEMENT_URL_CONTROL}`}>
                        {error => (
                            <FormInput
                                readOnly={readonly}
                                label="Advertisement URL"
                                placeholder="Advertisement URL"
                                value={settings.link || ''}
                                onBlur={changeAdvertisementLink}
                                valid={error && !settings.link ? false : undefined}
                                errorDescription={error && !settings.link
                                    ? 'Advertisement URL is required field'
                                    : undefined}
                            />
                        )}
                    </CustomValidatedControl>
                    <CustomValidatedControl name={`${type}-${ADVERTISEMENT_DISPLAY_TIME_CONTROL}`}>
                        {error => (
                            <NumberInput
                                maxValue={10}
                                readOnly={readonly}
                                label="Display time (in seconds)"
                                value={settings.showingTime}
                                onBlur={changeAdvertisementShowingTime}
                                displayedValue={settings.showingTime
                                    ? settings.showingTime.toString()
                                    : ''}
                                valid={error && !settings.showingTime ? false : undefined}
                                description={error && !settings.showingTime
                                    ? 'Display time is required'
                                    : undefined}
                            />
                        )}
                    </CustomValidatedControl>
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
